// public/v3/src/components/order-doc/lab-order-card.jsx — Task 9.
//
// The document-faithful Order Chat card for lab + quest_homebound orders.
// "Direction A" idea applied WITHIN the existing OrderCard look: the order is
// rendered as a faithful little requisition — a mode badge (Homebound draw vs
// e-Req), a per-test ICD-10 table with a confidence read, an "needs ICD"
// flag, a progress line, an "+ Add test" affordance, and a footer with the two
// actions the provider takes next. No letterhead / REQ-id / patient-provider
// meta chrome — that would diverge from order-chat.jsx's card style.
//
// The per-test dx EDITOR (Task 10): tapping a row's dx opens an inline
// FieldContextRail panel for THAT test — the SAME generic decision-support rail
// the fax form-fill screen drives, so coverage flags / covered-diagnosis options
// / search render identically here. The rail is READ-AND-CHOOSE: onPick(code)
// fills the dx via the `set-icd10` edit (it never auto-confirms); the search box
// re-interprets free text via `interpret`. The test NAME is a click-to-edit
// input that renames via `set-test-name` on a changed blur. add/remove rows go
// through `add-test` / `remove-test`. All edits flow up through the `onEdit`
// hook the page passes (it threads sessionId + orderIndex + this payload to
// ccmApi.orderEdit and re-renders strictly from the returned resolution):
//   onEdit({ action, payload:{ testIndex?, ... } })
//
// The inline editor (FieldContextRail, name-edit, Add/Remove) renders for BOTH
// `card.type === 'lab'` and `card.type === 'quest_homebound'`: the server edit
// path (`applyStructuredOrderEdit`) is quest-aware, so per-test edits apply to
// the quest home-draw test array too.
//
// Card shape (from voiceResolution.orderToCard, Tasks 5 + 13):
//   { type:'lab'|'quest_homebound', summary, tests[], vendor,
//     perTestResolution: [{ name, requested, code, description, status, confidence,
//                           source, candidates:[{code,description,status,source,onChart}] }] }
// `requested` is what the provider dictated when the matcher landed on a
// different compendium name ("hs-CRP" → "C-Reactive Protein, Cardiac"); null
// when they said the catalog name and for order-set tests.
// quest_homebound now ALSO carries perTestResolution (Task 13: the DTO surfaces
// Task 3's auto-resolved per-test dx + coverage), plus `homebound:true`. So the
// rows render with real dx for quest too — and are editable inline like lab.

import React, { useState, useRef, useEffect, useId } from 'react';
import {
  modeBadge, confidenceChip, warningLines, railContextForRow, scheduleLine,
  reviewSendLabel, blankDxWarning, orderDxPrintedLine, rowsKeyFor,
} from './order-doc-helpers.js';
import {
  alternativeOptions, moreLabel, nextActiveIndex, commitFor, swapAnnouncement, extraCodes,
  hintFor, gainedUnresolved,
} from './test-alternatives.js';
import { FieldContextRail } from '../field-context-rail.jsx';
import { coverageFlagText } from '../../lib/coverage-flag.js';
import { DestinationEditor } from './destination-editor.jsx';

const TONE_BADGE = {
  home:  { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' }, // homebound draw — blue
  ereq:  { bg: '#ecfdf5', fg: '#0f766e', border: '#99f6e4' }, // e-Req — teal (matches Elation pill)
};

const TONE_CHIP = {
  ok:     { bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0' },
  warn:   { bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
  danger: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
};

// The order's own disclosures, on the card. The chat bubble leads with the same
// strings ("Heads up — …"), but the card is what a provider reads when they are
// deciding whether to send — and it showed none of them, so a lab rerouted by a
// default looked like a lab they had chosen. Renders nothing when there are
// none. Exported so the page's summary card (non-lab orders) shows them too.
export function OrderWarnings({ lines }) {
  if (!Array.isArray(lines) || !lines.length) return null;
  const tone = TONE_CHIP.warn;
  return (
    <div className="orderdoc-warnings" style={{
      marginTop: 6, padding: '6px 8px',
      background: tone.bg, border: `1px solid ${tone.border}`,
      borderRadius: 8, color: tone.fg, fontSize: 12, lineHeight: 1.4,
    }}>
      {lines.map((w, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: i ? 4 : 0 }}>
          <span aria-hidden="true">⚠</span>
          <span>{w}</span>
        </div>
      ))}
    </div>
  );
}

// A per-test row is "confirmed" once its dx is grounded with real confidence —
// high/medium. low / null confidence (or a needs_dx status) still needs the
// provider to confirm the ICD-10. An `order_dx` row carries the order-level dx
// the requisition will fax for the whole panel → confirmed (and it has
// confidence:null, so it must be matched explicitly, not via confidence).
function isConfirmed(t) {
  if (!t) return false;
  if (t.status === 'needs_dx') return false;
  // A prepopulated best-guess dx (Task 2) is sendable but NOT auto-confirmed —
  // it still wants a provider glance, so it must not inflate the "N of N
  // confirmed" counter even when its confidence happens to read high/medium.
  if (t.status === 'suggested') return false;
  // An `order_dx` row is decided BEFORE the ambiguity clause below, and by the
  // same rule the chip uses: the order-level indication is the provider's own
  // choice for the whole draw (R-C-2), so nothing about a chart pick is being
  // deferred there.
  if (t.status === 'order_dx') return true;
  // (O69) An AMBIGUOUS auto-pick is covered and on the chart — and chosen by
  // nobody: two of the patient's diagnoses were equally covered for this test
  // and its indication families could not separate them, so the line carries
  // whichever Elation listed first. It stays sendable and keeps its code; it
  // does not count as confirmed until the provider has looked at the row.
  if (t.chartAmbiguous) return false;
  return t.confidence === 'high' || t.confidence === 'medium';
}

// Build the row model. Prefer perTestResolution (both lab AND quest carry it as
// of Task 13). Fall back to card.tests (a card with no resolution yet) so we
// still render the test names — those rows read as "needs ICD" since no code/
// confidence is present.
//
// INVARIANT: rows mirror the server test array 1:1 by POSITION, so a row's array
// index IS its `testIndex` for the per-test edit actions (set-icd10 / set-test-
// name / remove-test). Do not drop/reorder rows here — a future filter that
// removed a real test would desync `testIndex` against the server's array.
// Elation's Patient Condition enum → the label the order dialog shows.
const FASTING_LABELS = {
  fasting_12_hour: 'Fasting 12 hours', fasting_8_hours: 'Fasting 8 hours', fasting_2_hours: 'Fasting 2 hours',
  fasting_none: 'Not fasting', fasting_random: 'Fasting: does not matter',
};
function fastingLabel(v) { return FASTING_LABELS[v] || String(v); }

function rowsFor(card) {
  const res = Array.isArray(card.perTestResolution) ? card.perTestResolution : [];
  if (res.length) return res;
  const tests = Array.isArray(card.tests) ? card.tests : [];
  return tests.map((t) => ({
    name: (t && typeof t === 'object') ? (t.name || t.label || null) : t,
    code: null, description: null, status: null, confidence: null, source: null, candidates: [],
  }));
}

function ModeBadge({ badge }) {
  const c = TONE_BADGE[badge.tone] || TONE_BADGE.ereq;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
      color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
      padding: '2px 8px', borderRadius: 999,
    }}>
      {badge.label}
    </span>
  );
}

function ConfidenceChip({ confidence, status, ambiguous }) {
  // No resolution at all (quest fallback rows) → render the needs-ICD flag
  // directly rather than a misleading "Confirm" chip with no code.
  // An order_dx row inherits the order-level dx for the whole requisition — it
  // has confidence:null, so render a neutral, non-alarming "Order dx" chip (ok
  // tone) rather than the danger "Confirm" chip confidenceChip(null) would give.
  // A `suggested` row carries a prepopulated best-guess dx (Task 2). It is NOT
  // verified coverage — render it with the amber `warn` tone (never the green
  // `ok`/covered tone) and a label that reads as a guess to confirm, regardless
  // of the underlying confidence the guess happened to land on.
  // (O69) An AMBIGUOUS row is the same shape of claim one step milder: the code
  // IS covered (so "Suggested" would be false of it), and nothing about the test
  // chose it over the other charted diagnosis that is equally covered. Amber,
  // and it asks for the glance — the green "High" was a confidence nobody had.
  const isSuggested = status === 'suggested';
  const isOrderDx = status === 'order_dx';
  const isAmbiguous = !!ambiguous && !isSuggested && !isOrderDx && status !== 'needs_dx';
  const chip = confidenceChip(confidence);
  const tone = (isSuggested || isAmbiguous) ? 'warn' : (isOrderDx ? 'ok' : chip.tone);
  const c = TONE_CHIP[tone] || TONE_CHIP.danger;
  const label = status === 'needs_dx'
    ? 'Needs ICD'
    : (isSuggested ? 'Suggested · verify'
      : (isAmbiguous ? 'Covered · verify'
        : (isOrderDx ? 'Order dx' : chip.label)));
  return (
    <span style={{
      fontSize: 11, fontWeight: 600,
      color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
      padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// (F4; owner ruling O58, 2026-09-03) ONE indication for the whole home draw.
//
// The Quest requisition POOLS diagnoses — validate.js requires one ICD-10 across
// the order, and questHomebound/fromExtraction._icd prints the order's codes on
// every test that carries none — so one "this draw is for …" legitimately fills
// every blank line. Six of the eight lines on the owner's 2026-09-03 draw had no
// diagnosis and the only editor was the per-row rail: six opens, six picks, six
// round-trips. This is the order-level half of the same rail's job.
//
// PROVIDER-ENTERED, never derived: the field starts EMPTY and nothing is ever
// suggested into it (house rule (e) — never invent an indication). What the
// provider types is resolved SERVER-side against this patient's chart problems
// first, then the curated map, then an ICD-10 search (`set-order-icd10` →
// icd10.resolveDiagnosis), and only a RESOLVED CODE is ever stored: Quest's own
// form says "Only ICD-10 codes will be supported to process an order", so typed
// words must never reach it. An ambiguous text comes back as candidates to pick
// from with NOTHING written; a text that matches no ICD-10 says so.
//
// It never touches a line that already has its own code — that is the `_icd`
// fallback's own rule, and the house rule against backfilling an explicit pick.
function OrderIndication({ orderDx, candidates, unmatched, lines, onEdit }) {
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const hintId = `oc-order-dx-hint-${uid}`;
  const opts = Array.isArray(candidates) ? candidates.filter((c) => c && c.code) : [];
  // (r2/R1) Independent of `orderDx`: a REPLACEMENT that turns out ambiguous or
  // unmatched writes nothing and hands back a question, which the card owes the
  // provider whether or not an indication is already set. Gating these on "no
  // indication yet" swallowed the whole gesture — the old pill, and nothing else.
  const asking = opts.length > 0;
  const missed = !!unmatched;
  // ONE unrecognised code-shaped token (r2/R2) reads differently from a choice
  // between diagnoses: it is a confirmation, and the card must say what it is.
  const confirmExplicit = asking && opts.length === 1 && opts[0].source === 'explicit' && !opts[0].description;
  // Nothing set, nothing asked, and no line that needs one → no field: an order
  // where every test carries its own indication has nothing for this to do.
  if (!orderDx && !asking && !missed && !lines) return null;
  // The input shows while there is nothing to show instead, or when the provider
  // asked to (re)type. A commit always closes it: what comes back — a code, a
  // question, or a miss — is what the provider reads next.
  const showInput = editing || (!orderDx && !asking && !missed);

  async function commit(code) {
    if (inFlight.current || !onEdit) return;
    const typed = text.trim();
    if (!code && !typed) { setEditing(false); return; }   // empty submit = cancel, never a clear
    inFlight.current = true;
    setBusy(true);
    try {
      // Through the card's edit() wrapper: an order-level dx changes what every
      // blank line prints, so an open requisition preview must close before the
      // request leaves (R-A-6).
      await onEdit({ action: 'set-order-icd10', payload: code ? { code } : { text: typed } });
    } finally {
      inFlight.current = false;
      setBusy(false);
      setEditing(false);
      setText('');
    }
  }

  // (owner ruling O70) Remove — the explicit way to take the indication off the
  // order. Through the same wrapper, so the preview closes and the blank-line
  // warning re-arms; the server hands every line it was filling back to its own
  // chart pass.
  async function clear() {
    if (inFlight.current || !onEdit) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await onEdit({ action: 'set-order-icd10', payload: { clear: true } });
    } finally {
      inFlight.current = false;
      setBusy(false);
      setEditing(false);
      setText('');
    }
  }

  const btn = (extra) => ({
    type: 'button', ...extra,
  });
  return (
    <div data-testid="oc-order-dx" style={{
      marginTop: 8, padding: '6px 8px', background: '#fff',
      border: '1px solid #e5e7eb', borderRadius: 8,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: 0.3, textTransform: 'uppercase' }}>
        Diagnosis for the whole draw
      </div>

      {orderDx && !editing && (
        <div data-testid="oc-order-dx-set" style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600,
              color: '#047857', background: '#ecfdf5', border: '1px solid #a7f3d0',
              padding: '1px 7px', borderRadius: 999,
            }}>
              {orderDx.code}
            </span>
            {orderDx.description && <span style={{ fontSize: 12, color: '#374151' }}>{orderDx.description}</span>}
            <button
              {...btn({ onClick: () => { setText(''); setEditing(true); }, disabled: busy })}
              style={{
                marginLeft: 'auto', padding: '2px 8px', fontSize: 11, fontWeight: 600,
                color: '#0f766e', background: 'transparent',
                border: '1px solid #99f6e4', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Change
            </button>
            <button
              data-testid="oc-order-dx-remove"
              {...btn({ onClick: clear, disabled: busy })}
              aria-label="Remove the diagnosis for the whole draw"
              title="Remove this indication — the lines it fills go back to needing one"
              style={{
                padding: '2px 8px', fontSize: 11, fontWeight: 600,
                color: '#b91c1c', background: 'transparent',
                border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Remove
            </button>
          </div>
          {/* What it actually does to the document. `lines` is every line with no
              code of its OWN — the `order_dx` rows AND the R7/R8 row that keeps
              asking because its own dictated words resolved to nothing: `_icd`
              prints the order's codes on that one too, so the card may not
              undercount it (r2/R3). The sentence itself is the helper's, so the
              count and its grammar stay in one place. */}
          <div style={{ marginTop: 3, fontSize: 11, color: '#6b7280' }}>
            {orderDxPrintedLine(lines)}
          </div>
        </div>
      )}

      {showInput && (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              value={text}
              readOnly={busy}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') { e.preventDefault(); commit(null); }
                else if (e.key === 'Escape') { e.preventDefault(); setText(''); setEditing(false); }
              }}
              placeholder='This draw is for… (e.g. "hyperlipidemia")'
              aria-label="Indication for the whole draw"
              aria-describedby={hintId}
              style={{
                flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 13,
                border: '1px solid #99f6e4', borderRadius: 8, outline: 'none',
              }}
            />
            <button
              {...btn({ onClick: () => commit(null), disabled: busy || !text.trim() })}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#fff',
                background: (busy || !text.trim()) ? '#9ca3af' : '#0f766e',
                border: 'none', borderRadius: 8, cursor: (busy || !text.trim()) ? 'default' : 'pointer',
              }}
            >
              {busy ? 'Setting…' : 'Set'}
            </button>
            {(orderDx || asking || missed) && (
              <button
                {...btn({ onClick: () => { setText(''); setEditing(false); } })}
                style={{
                  padding: '5px 10px', fontSize: 12, fontWeight: 600, color: '#374151',
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <div id={hintId} style={{ marginTop: 3, fontSize: 11, color: '#6b7280' }}>
            {lines
              ? `Fills the ${lines} ${lines === 1 ? 'test' : 'tests'} with no diagnosis of ${lines === 1 ? 'its' : 'their'} own. Lines you set yourself are left alone.`
              : 'Fills any test with no diagnosis of its own. Lines you set yourself are left alone.'}
          </div>
        </div>
      )}

      {asking && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 12, color: '#b45309' }}>
            {confirmExplicit
              ? `Confirm this code — ${opts[0].code} is not one we recognise.`
              : 'Which one?'}
            {orderDx
              /* (r2/R1) The old code is still what the requisition carries — say
                 so, or a replacement that came back as a question reads as if it
                 had already changed the fax. */
              ? ` ${orderDx.code} stays until you pick.`
              : ' Nothing is set until you pick.'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {opts.map((c) => (
              <button
                key={c.code}
                data-testid="oc-order-dx-candidate"
                {...btn({ onClick: () => commit(c.code), disabled: busy })}
                title={c.description || undefined}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 6,
                  padding: '3px 8px', fontSize: 12, color: '#374151',
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 999,
                  cursor: busy ? 'default' : 'pointer',
                }}
              >
                <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: 600 }}>{c.code}</span>
                {c.description && <span>{c.description}</span>}
                {/* What this candidate IS. Only the chart makes a claim here: the
                    order-level indication is not coverage-checked against any
                    test, so nothing else may read as verified. */}
                {c.source === 'chart' && (
                  <span style={{ fontSize: 11, color: '#047857' }}>· On this patient’s chart</span>
                )}
              </button>
            ))}
          </div>
          {!editing && (
            <button
              {...btn({ onClick: () => { setText(''); setEditing(true); } })}
              style={{
                marginTop: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                color: '#0f766e', background: 'transparent',
                border: '1px dashed #99f6e4', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Type another indication
            </button>
          )}
        </div>
      )}

      {missed && (
        <div data-testid="oc-order-dx-unmatched" role="note" style={{
          marginTop: 4, padding: '4px 8px', fontSize: 12, lineHeight: 1.35,
          color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
        }}>
          no ICD-10 matched “{unmatched}” — {orderDx
            ? `${orderDx.code} stays on the requisition; try other words`
            : 'try other words, or set each test’s diagnosis on its own row'}
          {!editing && (
            <button
              {...btn({ onClick: () => { setText(''); setEditing(true); } })}
              style={{
                marginLeft: 8, padding: '1px 8px', fontSize: 11, fontWeight: 600,
                color: '#0f766e', background: '#fff',
                border: '1px solid #99f6e4', borderRadius: 6, cursor: 'pointer',
              }}
            >
              Try again
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// O4 — the test name is an APG editable COMBOBOX: clicking it (or the quiet
// "▾ N more" chip beside it) turns it into a text field with a listbox of the
// compendium rows the matcher actually considered for this line. One widget
// covers both things a provider wants from a name: rename it (unchanged
// `set-test-name`), or ORDER ONE OF THE OTHERS.
//
// Why a listbox and not a menu: this chooses a VALUE and has a current
// selection ("Keep X" is option 0, so a stray Enter changes nothing).
// Why not a native <select>: it cannot render name · vendor code · reason, and
// the rename must live in the same control.
//
// A swap is an IDENTITY change, not a re-resolve: `pick-test-alternative`
// carries the row's id plus `fromTestId` — the id the card rendered this line
// from. The engine 400s a stale or absent `fromTestId`, and that 400 means "this
// card is out of date", so it is shown in place with the list left open rather
// than reported as a failed order.
//
// Every decision here (which option is active, what a keystroke commits, what
// gets announced) lives in test-alternatives.cjs, which is unit-tested; this
// component only renders those descriptors.
function TestNameCombobox({ t, index, onEdit, onAnnounce }) {
  const name = t.name || '';
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(name);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [staleError, setStaleError] = useState(null);
  const nameRef = useRef(null);
  const inFlight = useRef(false);
  // Focus bookkeeping. `focusLeft` records that focus has already left the
  // widget (a Tab-out, or a click elsewhere while an edit was in flight);
  // `wantRefocus` is what the close DECIDED, read back by the effect below.
  const focusLeft = useRef(false);
  const wantRefocus = useRef(false);
  // Unique per rendered row: two lab cards in one turn both have a row 0, and
  // aria-controls / aria-activedescendant must not cross-reference them.
  // Sanitised because useId()'s colons are legal in an id but break selectors.
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const listId = `oc-alt-list-${uid}`;
  const hintId = `oc-alt-hint-${uid}`;

  const options = alternativeOptions(t);
  const more = moreLabel(t);
  const listOpen = open && options.length > 1;

  // The server is the single source of truth for the name: after a swap or a
  // rename the row re-renders with the new one, so the field must follow it
  // (the widget stays mounted — its key is the row's index).
  useEffect(() => { setVal(t.name || ''); }, [t.name]);

  // Focus return happens in a RENDER EFFECT, so the button provably exists by
  // the time it is focused: the base editor let focus fall to <body> on close,
  // and a requestAnimationFrame callback would race the scheduled render that
  // closing after a response produces under createRoot.
  useEffect(() => {
    if (!open && wantRefocus.current) {
      wantRefocus.current = false;
      if (nameRef.current) nameRef.current.focus();
    }
  }, [open]);

  function openList() {
    setStaleError(null);
    setVal(t.name || '');
    // Active starts on "keep", so a stray Enter is never a swap.
    setActive(options.length > 1 ? 0 : -1);
    focusLeft.current = false;
    wantRefocus.current = false;
    setOpen(true);
  }

  function close(refocus) {
    // Only an Enter / Escape / click close hands focus back to the name. A
    // Tab-out — or a click into the composer while an edit was in flight, which
    // `inFlight` refuses to commit — has already put focus where the provider
    // aimed it, and the response must not yank it back.
    wantRefocus.current = !!refocus && !focusLeft.current;
    setOpen(false);
    setActive(-1);
    setStaleError(null);
    setVal(t.name || '');
  }

  async function commit(reason, activeIndex) {
    if (inFlight.current) return;
    const refocus = reason !== 'blur';
    const c = commitFor({
      reason, row: t, testIndex: index,
      activeIndex: activeIndex === undefined ? active : activeIndex,
      text: val,
    });
    if (!c.edit) { close(refocus); return; }
    inFlight.current = true;
    setBusy(true);
    setStaleError(null);
    let res;
    try {
      res = await onEdit(c.edit);
    } catch (e) {
      res = { ok: false, error: (e && e.message) || null };
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
    if (res && res.ok === false) {
      if (res.stale) {
        // A refused swap is a STALE CARD, not a failed order. The page has
        // already installed the session's FRESH rows, so reopening the list
        // offers what is actually orderable now — reopening the same rows was a
        // dead end that re-refused every retry. The role="alert" node speaks the
        // route's own sentence, so nothing is pushed to the live region beside
        // it.
        setStaleError(res.error || null);
        setActive(0);
        setOpen(true);
        return;
      }
      // Every other failure was reported in chat by the page.
      close(refocus);
      return;
    }
    onAnnounce(swapAnnouncement(c));
    close(refocus);
  }

  if (!open) {
    return (
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, flex: '1 1 auto', minWidth: 0 }}>
        <button
          type="button"
          ref={nameRef}
          onClick={(e) => { e.stopPropagation(); openList(); }}
          onKeyDown={(e) => { e.stopPropagation(); }}
          title={more ? `Rename this test, or choose one of ${more.count} alternatives` : 'Rename this test'}
          style={{
            flex: '0 1 auto', minWidth: 0, textAlign: 'left',
            fontWeight: 600, fontSize: 13, color: '#111',
            background: 'transparent', border: 'none', padding: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text',
          }}
        >
          {t.name || '(unnamed test)'}
        </button>
        {/* The vendor code, on the row, whenever the line has one. Without it a
            swap between two same-name rows leaves the card unchanged to the eye:
            the announcement is transient and the list must be reopened to read
            which row is "current". One rule — quest / generic / unmatched lines
            carry no code and render nothing. */}
        {t.testCode && (
          <span className="oc-alt-code" title="Vendor test code">{t.testCode}</span>
        )}
        {/* The ONLY closed-state hint that other rows were considered — quiet,
            never a forced picker (O4). Absent when the matcher had no choice. */}
        {more && (
          <button
            type="button"
            className="oc-more"
            data-testid="oc-more"
            aria-label={more.ariaLabel}
            onClick={(e) => { e.stopPropagation(); openList(); }}
            onKeyDown={(e) => { e.stopPropagation(); }}
          >
            <span aria-hidden="true">▾</span> {more.text}
          </button>
        )}
      </span>
    );
  }

  const hint = hintFor({ text: val, name, active, listOpen, more });

  return (
    <span
      className="oc-alt-wrap"
      aria-busy={busy || undefined}
      // The whole open widget sits INSIDE the row's own role="button" (which
      // toggles the dx rail), so every click in here must stop there —
      // otherwise choosing an alternative would also open/close the rail.
      onClick={(e) => e.stopPropagation()}
      style={{ flex: '1 1 auto', minWidth: 0 }}
    >
      <input
        autoFocus
        type="text"
        value={val}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listOpen}
        aria-controls={listId}
        aria-activedescendant={listOpen && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label="Test name — type to rename, or choose an alternative"
        aria-describedby={hintId}
        // readOnly, NOT disabled: disabling the focused input fires blur, which
        // would commit a SECOND edit for the one the provider just made.
        readOnly={busy}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => { setVal(e.target.value); setActive(-1); }}
        onBlur={() => { focusLeft.current = true; commit('blur'); }}
        onKeyDown={(e) => {
          e.stopPropagation();
          const moved = nextActiveIndex(e.key, active, listOpen ? options.length : 0);
          if (moved !== null) { e.preventDefault(); setActive(moved); return; }
          if (e.key === 'Enter') { e.preventDefault(); commit('enter'); return; }
          if (e.key === 'Escape') { e.preventDefault(); commit('escape'); }
          // Tab falls through: the browser moves focus and onBlur commits a
          // changed name — never a pick.
        }}
        style={{
          width: '100%', minWidth: 0, boxSizing: 'border-box',
          fontWeight: 600, fontSize: 13, color: '#111',
          border: '1px solid #99f6e4', borderRadius: 6, padding: '3px 6px',
        }}
      />
      {/* One node, two jobs: the listbox usage hint (visually hidden, read on
          focus) becomes the VISIBLE status line once the provider starts typing,
          so "Enter renames / pick below" cannot be missed. */}
      <span id={hintId} className={hint.typing ? 'oc-alt-status' : 'oc-sr-only'}>{hint.text}</span>
      {listOpen && (
        <ul
          className="oc-alt-list"
          data-testid="oc-alt-list"
          role="listbox"
          id={listId}
          aria-label={`Alternatives for ${name || 'this test'}`}
        >
          {options.map((o) => (
            <li
              key={o.id}
              id={`${listId}-${o.index}`}
              role="option"
              aria-selected={o.index === active}
              className={'oc-alt-item' + (o.index === active ? ' is-active' : '') + (o.current ? ' is-current' : '')}
              // mousedown + preventDefault: blur commits a RENAME, and if it
              // landed before the click the pick would be swallowed (the
              // composer's patient-suggest trick). It orders exactly the option
              // clicked, because it reads o.index and never `active`.
              //
              // NO onMouseEnter. Chrome dispatches boundary events to whatever
              // appears under a STATIONARY pointer after a layout change, so a
              // list opening beneath a parked cursor moved the Enter target off
              // "keep" with no pointer movement at all — and the next Enter
              // ordered whatever sat under the mouse (browser QA reached a
              // genetic sequencing panel from `Apolipoprotein B`). Hover is
              // therefore visual only, in CSS; `aria-activedescendant` follows
              // the keyboard alone.
              onMouseDown={(e) => { e.preventDefault(); commit('select', o.index); }}
            >
              <span className="oc-alt-name">{o.label}</span>
              {/* Never optional: a same-name group differs ONLY by vendor code. */}
              <span className="oc-alt-code">{o.codeText}</span>
              <span className="oc-alt-why">{o.reason}</span>
              {/* O24: what the row IS — "CPT 82306 · added 2017 · asks 2
                  questions". A SECOND line, not a fourth column: the name
                  column already ellipsizes (.oc-alt-name has min-width:0), and
                  30 more characters on that row would eat the assay name the
                  provider is choosing by. Inside the same li[role=option], so a
                  screen reader reads name, code, reason, then this. Absent when
                  the row says nothing — a pre-072 row, an order-set row, a
                  quest row — rather than an empty line. */}
              {o.meta && <span className="oc-alt-meta">{o.meta}</span>}
            </li>
          ))}
        </ul>
      )}
      {staleError && (
        <div role="alert" className="oc-alt-error">{staleError}</div>
      )}
    </span>
  );
}

function TestRow({ t, index, expanded, onToggle, onEdit, editable, onAnnounce }) {
  const hasCode = !!t.code;
  const extras = extraCodes(t);
  // The exact "Why" for THIS row: does the seeded code satisfy medical necessity
  // for this test? Null → no flag (same contract the rail uses internally).
  const covFlag = coverageFlagText(t.status, t.code, t.name);
  const COV_TONE = {
    ok:     { fg: '#047857', bg: '#ecfdf5', border: '#a7f3d0' },
    warn:   { fg: '#b45309', bg: '#fffbeb', border: '#fde68a' },
    danger: { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  };
  const flagTone = covFlag ? (COV_TONE[covFlag.tone] || COV_TONE.warn) : null;

  // Build the generic field-context for this test from its candidates — the
  // SAME normalized shape fetchFieldOptions emits for the fax rail, including
  // (F12) the ONE tier advisory the rail shows above the options.
  const context = railContextForRow(t);
  const answer = { value: t.code, coverage: { status: t.status } };
  // (F8) The draw schedule this line was dictated with, display-only.
  const schedule = scheduleLine(t.schedule);

  // Editable rows (lab + quest_homebound) expand into the inline editor.
  const canEdit = editable && !!onEdit;
  return (
    <div style={{ borderTop: '1px solid #f3f4f6' }}>
      <div
        {...(canEdit ? {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': expanded,
          onClick: () => onToggle(index),
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(index); }
          },
        } : {})}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          // Containing block for the alternatives listbox: the widget's own wrap
          // is `flex: 1 1 auto` between flex-shrink:0 siblings, so anchoring the
          // list to it made the list as narrow as the name cell's leftover width.
          position: 'relative',
          padding: '7px 4px',
          cursor: canEdit ? 'pointer' : 'default',
        }}
      >
        {canEdit && (
          <span aria-hidden="true" style={{ color: '#9ca3af', fontSize: 11, flexShrink: 0, width: 10 }}>{expanded ? '▾' : '▸'}</span>
        )}
        {canEdit ? (
          <TestNameCombobox
            t={t}
            index={index}
            onEdit={onEdit}
            onAnnounce={onAnnounce || (() => {})}
          />
        ) : (
          <span style={{ flex: '1 1 auto', minWidth: 0, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t.name || '(unnamed test)'}
          </span>
        )}
        {/* The matcher reached a name the provider never said — show what they
            dictated next to it so the substitution is reviewable at confirm. */}
        {t.requested && (
          <span
            title={`You said "${t.requested}" — ordering "${t.name}"`}
            style={{
              fontSize: 11, color: '#6b7280', fontStyle: 'italic',
              flexShrink: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            you said “{t.requested}”
          </span>
        )}
        {/* The provider dictated an indication for THIS test that resolved to no
            code — keep their words visible so the gap is theirs to close, never a
            silent drop (R7/R8). */}
        {t.dictatedDx && !t.code && (
          <span
            title={`Dictated indication: "${t.dictatedDx}" — no ICD-10 matched; set it here`}
            style={{ fontSize: 11, color: '#b45309', fontStyle: 'italic', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            dictated “{t.dictatedDx}”
          </span>
        )}
        <span style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
          color: hasCode ? '#374151' : '#9ca3af', flexShrink: 0,
        }} title={t.description || undefined}>
          {hasCode ? t.code : '— no code'}
        </span>
        {/* Surface the ICD-10 descriptor inline (it used to live only in the
            code span's `title` tooltip) so the provider can read WHAT the code
            is at a glance. Stays single-line, truncates with an ellipsis. */}
        {hasCode && t.description && (
          <span
            title={t.description}
            style={{
              fontSize: 11, color: '#6b7280',
              flexShrink: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {t.description}
          </span>
        )}
        {/* OF-659-1: every indication the provider dictated for this line now
            rides it, so a line can carry more than one ICD-10. codes[0] IS the
            primary the confidence chip describes; the rest are chips, so a
            second indication is visible at the confirm gate instead of only
            reaching the requisition. */}
        {extras.map((c) => (
          <span
            key={c.code}
            className="oc-code-chip"
            title={c.description || undefined}
          >
            + {c.code}
          </span>
        ))}
        <ConfidenceChip confidence={t.confidence} status={t.status} ambiguous={t.chartAmbiguous} />
      </div>

      {/* F8 — the fasting / frequency / start / finish this line was dictated
          with. They print into the requisition's REQUIRED columns, so the
          provider has to be able to read them before it goes. DISPLAY-ONLY:
          changing them is a follow-up (the standing trio is all-or-nothing and
          6-month bounded, so an editor owes its own validation). */}
      {schedule && (
        <div data-testid="oc-row-schedule" style={{
          margin: '0 4px 6px 24px', fontSize: 11, color: '#6b7280',
        }}>
          {schedule}
        </div>
      )}

      {/* H1 — the test name couldn't be matched to a catalog id, so it can't be
          ordered as-is (it would be silently dropped at execution). Surface it
          distinctly so the provider renames or removes it BEFORE confirming. */}
      {t.needsResolution && (
        <div role="note" style={{
          margin: '0 4px 6px 24px', padding: '4px 8px', fontSize: 12, lineHeight: 1.35,
          color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
        }}>
          ⚠ couldn't match this test — rename or remove it
        </div>
      )}

      {/* The exact medical-necessity "Why" for this row, shown near it. */}
      {covFlag && (
        <div role="note" style={{
          margin: '0 4px 6px 24px', padding: '4px 8px', fontSize: 12, lineHeight: 1.35,
          color: flagTone.fg, background: flagTone.bg, border: `1px solid ${flagTone.border}`, borderRadius: 6,
        }}>
          {covFlag.text}
        </div>
      )}

      {/* Inline per-test dx editor — the SAME generic rail the fax form-fill
          screen drives.
          onPick fills the dx (set-icd10); the search re-interprets free text
          (interpret) — but NOT on an empty query (FieldContextRail fires
          onSearch('') once on mount; an empty interpret is a wasted mutating
          round-trip). The rail NEVER auto-confirms. */}
      {canEdit && expanded && (
        <div style={{ margin: '0 4px 8px 24px' }}>
          {/* oc-fcr-inline resets the rail's desktop side-column CSS (position:
              sticky + its own max-height/overflow scroll) so inside this card it
              lays out INLINE — otherwise the rail's internal scroll hides the
              interpret/search box and the sticky rail overlaps the Remove button
              below. Mirrors the fax screen's .fax-form-fill-work override. */}
          <div className="oc-fcr-inline">
          <FieldContextRail
            field={{ id: `test-${index}`, label: t.name, fieldType: 'icd10' }}
            answer={answer}
            context={context}
            loading={false}
            onPick={(code) => onEdit({ action: 'set-icd10', payload: { testIndex: index, code } })}
            onSearch={(q) => { if (q && q.trim()) onEdit({ action: 'interpret', payload: { testIndex: index, text: q.trim() } }); }}
            mode="rail"
          />
          </div>
          <button
            type="button"
            onClick={() => onEdit({ action: 'remove-test', payload: { testIndex: index } })}
            style={{
              marginTop: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
              color: '#b91c1c', background: 'transparent',
              border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer',
            }}
          >
            Remove test
          </button>
        </div>
      )}
    </div>
  );
}

// rowLive: this card's SESSION row is still live (`partially_sent`) as the
// server last stated it. Read once, at the footer: a requisition the batch
// drafted but never faxed can still be faxed from its own card, because the
// route reuses the report it already wrote (#694's ledger gate) instead of
// making a second one. A finished row is not offered it — the route would 409,
// and a control the server refuses is the dishonesty this arc keeps removing.
// `control` (R-CF-7 / D4, PR-CF-P1): whether the confirm primitive would ACCEPT
// this card right now, and the gate's own sentence when it would not. The
// footer used to offer its action unconditionally and learn the answer as a
// 409; a control the server refuses is not a control (R-S5-52). Absent on a
// reply from before this slice — the button then renders as it always did.
export function LabOrderCard({ card, draftId, onEdit, onSubmit, onPreview, onSend, sent, rowLive, control }) {
  const rows = rowsFor(card);
  const total = rows.length;
  const confirmed = rows.filter(isConfirmed).length;
  const allConfirmed = total > 0 && confirmed === total;
  // F3: "Review & send" is ALWAYS active — it opens the requisition preview,
  // which is also the only place a prerequisite failure (no Quest account #,
  // unresolved physician) is ever shown. A row without a code changes the LABEL,
  // never the colour. F9/O61: the blank lines get a warning naming the count;
  // draft and fax both stay allowed and the provider decides.
  // (F4) Both counts are about what the requisition PRINTS, so both read the
  // order-level indication: a line with no code of its own is not blank when the
  // order carries one — the `_icd` fallback prints it there.
  const sendLabel = reviewSendLabel(rows, card.orderDx);
  const dxWarning = blankDxWarning(rows, card.orderDx);
  // (r2/R3) The lines the order-level indication is printed on — ONE count, the
  // fax's: every line with no code of its own. That is the `order_dx` rows plus
  // the R7/R8 row that keeps asking (its own dictated words resolved to nothing),
  // which `_icd` fills from the order like any other code-less line. With no
  // indication set it is simply the lines that need one.
  const fallbackLines = rows.filter((r) => r && (r.status === 'order_dx' || !r.code)).length;
  // Quest is now editable too (server applyStructuredOrderEdit is quest-aware).
  const editable = card.type === 'lab' || card.type === 'quest_homebound';
  // Recipient-routed cards (a facility-routed lab/imaging requisition) carry an
  // editable Send-to destination. Quest is its own fixed Mobile-Phlebotomy fax
  // flow, so the editor renders only for the non-quest recipient-routed case.
  const recipientRouted = !!(card.sendToFacility || card.recipientContact);
  // Generic (non-connected) lab tagged by genericLabsConfig: gets the same
  // Review & send direct-fax flow as Quest, plus an editable destination fax.
  const isGeneric = card.type === 'lab' && !!card.genericLab;
  const showDestination = recipientRouted && card.type !== 'quest_homebound' && !isGeneric;
  const recipientContact = card.recipientContact || {};
  // A requisition this thread already DRAFTED and never faxed, on a row the
  // server still calls live: its Review & send stays under the badge, because
  // the send routes reuse the drafted report instead of writing a second one
  // (#694's ledger gate). Every other filed card shows the badge alone. With no
  // roster — every single-patient turn — `rowLive` is undefined and nothing
  // about this card changes.
  const faxableAfterDraft = !!(sent && !sent.sent && rowLive && (card.type === 'quest_homebound' || isGeneric));
  // One row's dx editor open at a time (accordion). Index into `rows`.
  const [openRow, setOpenRow] = useState(null);
  const toggleRow = (i) => setOpenRow((cur) => (cur === i ? null : i));

  // O4 — ONE polite live region for the whole card, so a swap, a rename or a
  // refusal is spoken rather than only redrawn. A repeat of the identical
  // sentence (clicking a stale option twice) gets a zero-width space so the
  // region's content still CHANGES and is announced again.
  const [announcement, setAnnouncement] = useState('');
  function announce(text) {
    setAnnouncement((prev) => (text && prev === text ? `${text}\u200B` : text));
  }
  // A rename that matched no catalog row draws the H1 ⚠ note but said nothing;
  // announce the moment a row GAINS that state (never on first render, since the
  // ref starts as the rows this render already shows). Compared by NAME, so a
  // miss renamed to another miss is a new event and a surviving miss is not
  // re-announced when a row above it is removed — the dependency is the
  // unresolved NAMES, so a render that does not change them cannot re-fire.
  const prevRows = useRef(rows);
  const unresolvedKey = rows.filter((r) => r && r.needsResolution).map((r) => r.name || '').join('|');
  useEffect(() => {
    const gained = gainedUnresolved(prevRows.current, rows);
    prevRows.current = rows;
    if (gained.length) announce(swapAnnouncement({ kind: 'unresolved' }));
  }, [unresolvedKey]);

  // "+ Add test" opens an INLINE text field (not a blocking window.prompt).
  const [addingTest, setAddingTest] = useState(false);
  const [newTestName, setNewTestName] = useState('');

  // B2 — quest/generic "Review & send": fetch the requisition via onPreview,
  // render it inline (base64→blob→iframe), and fax only on an explicit Confirm
  // click. preview: null | { loading:true } | { url } | { errors }
  const [preview, setPreview] = useState(null);
  // Generic-lab destination fax, seeded from the configured number when the
  // preview loads; editable for a one-off override at send time.
  const [faxEdit, setFaxEdit] = useState('');

  // In-flight guard for the quest submit actions (Confirm / Confirm & fax).
  // onSubmit resolves to the async submitQuestOrder(...) — gate re-entry on it so
  // a concurrent double-click can't reach the server twice before the first
  // transitionSession(...,'completed') commits (two drafts/reports / double-fax).
  const [submitting, setSubmitting] = useState(false);
  // Every card edit is persisted by the route BEFORE its response installs the
  // new rows here, so for that round-trip a rendered PDF describes a session that
  // has already moved on — and "Confirm & fax", which lives inside the pane, was
  // still live (the rowsKey effect fires one response too late). So ONE wrapper
  // owns every edit: it closes the preview SYNCHRONOUSLY, before the request
  // leaves — unmounting the confirm with it — and counts itself in flight so
  // Review & send cannot render a PDF of the pre-edit state either.
  const [editsInFlight, setEditsInFlight] = useState(0);
  async function edit(e) {
    if (!onEdit) return undefined;
    closePreview();
    setEditsInFlight((n) => n + 1);
    try { return await onEdit(e); } finally { setEditsInFlight((n) => n - 1); }
  }
  async function doSubmit(arg) {
    if (submitting || !onSubmit) return;
    setSubmitting(true);
    try { await onSubmit(arg); } finally { setSubmitting(false); }
  }

  // "Send now" on the non-quest (facility-routed) footer: a two-step inline
  // confirm that shows the destination first (there's no requisition PDF to
  // preview, unlike Quest), then signs + transmits via onSend('send'). Save draft
  // keeps the existing unsigned-draft behavior via onSend('draft'). sending gates
  // re-entry so a double-click can't double-submit.
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [sending, setSending] = useState(false);
  async function doSend(mode) {
    if (sending || !onSend) return;
    setSending(true);
    try { await onSend(mode); } finally { setSending(false); setConfirmingSend(false); }
  }

  // Generation counter for in-flight previews. The rows stay editable while
  // onPreview() is pending, so an edit mid-flight used to close the {loading}
  // pane and then have the LATE response reopen the old PDF over the edited
  // session — and Confirm & fax would send the edited order. Every close (the
  // rows-changed effect included) bumps the generation, so a response from a
  // superseded request is dropped instead of rendered.
  const previewGen = useRef(0);
  async function openPreview() {
    if (submitting) return;
    if (editsInFlight) return;   // a PDF of the pre-edit state is a lie
    if (!onPreview) return;
    const gen = ++previewGen.current;
    setPreview({ loading: true });
    try {
      const r = await onPreview();
      if (gen !== previewGen.current) return;
      if (r && r.pdfBase64) {
        const blob = new Blob([Uint8Array.from(atob(r.pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
        setPreview({ url: URL.createObjectURL(blob), recipientName: r.recipientName || null, fax: r.fax || null, warnings: r.warnings || [] });
        setFaxEdit(r.fax || '');
      } else {
        setPreview({ errors: (r && r.errors) || ['Could not render the requisition.'] });
      }
    } catch (e) {
      if (gen !== previewGen.current) return;
      setPreview({ errors: [(e && e.message) || 'Could not render the requisition.'] });
    }
  }
  function closePreview() {
    previewGen.current += 1;
    if (preview && preview.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }
  // The PDF on screen is a snapshot; "Confirm & fax" rebuilds the send from the
  // SESSION. An inline rename / dx pick / add / remove round-trips to the server
  // and comes back as new rows, so a preview left open would sit above a document
  // that is no longer what would be faxed (codex C1, adjudicated r2 #1). Any
  // change to what a line would PRINT (its name, its resolution status, any of its
  // ICD-10s — see rowsKeyFor) therefore CLOSES it: the provider re-opens Review &
  // send and confirms under a PDF rendered from the current rows. Closing also
  // bumps the preview generation, so a request still in flight is discarded.
  const rowsKey = rowsKeyFor(rows, card.orderDx);
  useEffect(() => {
    if (preview) closePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the ROWS are the trigger
  }, [rowsKey]);
  function confirmFax() {
    // A generic-lab fax edited away from the configured number rides along as a
    // one-off override; unchanged (or quest) sends nothing extra.
    const edited = isGeneric && faxEdit.replace(/\D/g, '') !== String(preview && preview.fax || '').replace(/\D/g, '')
      ? faxEdit.replace(/\D/g, '')
      : undefined;
    closePreview();
    doSubmit(edited ? { mode: 'fax', fax: edited } : { mode: 'fax' });
  }
  // Generic cards must have SOME fax to send to (configured or typed).
  const faxDigits = faxEdit.replace(/\D/g, '');
  const canConfirmFax = !isGeneric || (faxDigits.length >= 10 && faxDigits.length <= 11);

  // "+ Add test" reveals an inline field; committing resolves the new test's dx
  // server-side via the add-test action. Empty → just closes (cancel). Replaces
  // the old blocking window.prompt with in-card UX.
  function commitAddTest() {
    const trimmed = newTestName.trim();
    if (trimmed) edit({ action: 'add-test', payload: { name: trimmed } });
    setNewTestName('');
    setAddingTest(false);
  }
  function cancelAddTest() {
    setNewTestName('');
    setAddingTest(false);
  }

  return (
    <div style={{
      marginTop: 6,
      padding: '10px 12px',
      background: '#fafafa',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      fontSize: 13,
    }}>
      {/* Header: icon + title + mode badge + Elation draft pill (parity w/ OrderCard) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>🧪</span>
        <span style={{ fontWeight: 600 }}>
          {card.type === 'quest_homebound' ? 'Quest homebound order' : `Lab order${card.vendor ? ` (${card.vendor})` : ''}`}
        </span>
        <ModeBadge badge={modeBadge(card.type)} />
        {draftId != null && (
          <span style={{
            marginLeft: 'auto', fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 11, color: '#0f766e', background: '#ecfdf5',
            padding: '2px 6px', borderRadius: 4,
          }}>
            Elation #{draftId}
          </span>
        )}
      </div>

      {/* O4 — what a swap / rename / refusal says out loud. */}
      <span className="oc-sr-only" aria-live="polite" aria-atomic="true">{announcement}</span>

      {/* Why this order routes where it does (default/substituted lab, coverage,
          allergy) — the same strings the chat text leads with. */}
      <OrderWarnings lines={warningLines(card)} />

      {/* Progress: N of M confirmed */}
      <div style={{
        marginTop: 6, fontSize: 12,
        color: allConfirmed ? '#047857' : '#b45309',
      }}>
        {total === 0
          ? 'No tests on this order yet'
          : `${confirmed} of ${total} confirmed`}
        {editable && total > 0 && !allConfirmed && (
          <span style={{ color: '#9ca3af' }}> · tap a test to set its diagnosis</span>
        )}
      </div>

      {/* F4/O58 — ONE indication for the whole draw, above the rows it fills.
          Home draw only: a LAB order's order-level dx is derived from the
          dictation and reconciled against the per-test ones (O8), so the card
          does not edit it. */}
      {card.type === 'quest_homebound' && (
        <OrderIndication
          orderDx={card.orderDx || null}
          candidates={card.orderDxCandidates}
          unmatched={card.orderDxUnmatched}
          lines={fallbackLines}
          onEdit={edit}
        />
      )}

      {/* G3: order-level prep the requisition will carry (lab-facing test_center_notes)
          and the structured fasting the dictation set — reviewable before Save draft. */}
      {(card.instructions || card.fasting) && (
        <div data-testid="lab-prep" style={{ marginTop: 6, fontSize: 12, color: '#374151' }}>
          {card.fasting && <span style={{ fontWeight: 600 }}>{fastingLabel(card.fasting)}</span>}
          {card.fasting && card.instructions && <span style={{ color: '#9ca3af' }}> · </span>}
          {card.instructions && <span>Lab note: “{card.instructions}”</span>}
        </div>
      )}

      {/* Per-test ICD-10 table: name · code · confidence; each row expands into
          the inline FieldContextRail dx editor. */}
      {total > 0 && (
        <div style={{ marginTop: 6 }}>
          {rows.map((t, i) => (
            <TestRow
              key={i}
              t={t}
              index={i}
              expanded={openRow === i}
              onToggle={toggleRow}
              onEdit={edit}
              editable={editable}
              onAnnounce={announce}
            />
          ))}
        </div>
      )}

      {/* + Add test affordance — renders for any editable card (lab + quest).
          Clicking reveals an INLINE field (no blocking window.prompt). */}
      {editable && !addingTest && (
        <button
          type="button"
          onClick={() => setAddingTest(true)}
          style={{
            marginTop: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600,
            color: '#0f766e', background: 'transparent',
            border: '1px dashed #99f6e4', borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          + Add test
        </button>
      )}
      {editable && addingTest && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="text"
            autoFocus
            value={newTestName}
            onChange={(e) => setNewTestName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitAddTest(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelAddTest(); }
            }}
            onBlur={() => { if (!newTestName.trim()) cancelAddTest(); }}
            placeholder='Test name (e.g. "CBC", "Lipid panel")'
            aria-label="New test name"
            style={{
              flex: 1, minWidth: 0, padding: '6px 10px', fontSize: 13,
              border: '1px solid #99f6e4', borderRadius: 8, outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={commitAddTest}
            disabled={!newTestName.trim()}
            style={{
              padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#fff',
              background: newTestName.trim() ? '#0f766e' : '#9ca3af',
              border: 'none', borderRadius: 8, cursor: newTestName.trim() ? 'pointer' : 'default',
            }}
          >
            Add
          </button>
          <button
            type="button"
            onClick={cancelAddTest}
            style={{
              padding: '6px 10px', fontSize: 12, fontWeight: 600, color: '#374151',
              background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Editable Send-to destination — only for recipient-routed (facility)
          lab/imaging requisitions. Threads through the same onEdit hook every
          other per-card edit uses, as the `set-destination` action. */}
      {showDestination && (
        <DestinationEditor
          value={{ fax: recipientContact.fax, email: recipientContact.email }}
          onSet={(channel, value) => edit({ action: 'set-destination', payload: { channel, value } })}
        />
      )}

      {/* Footer actions. Quest + generic (non-connected) labs get the Review &
          send / Save draft direct-fax flow (B2); other labs get the per-card
          "Save draft" action (M3) that dispatches this one order via onSend,
          then flips read-only once sent. */}
      {sent && (
        /* Filed already: the badge. A Quest / generic card's own fax footer used
           to render before this branch was ever reached, so a card the batch had
           filed kept offering to file it again — and showed no id (codex,
           R-S5-48). It is the whole footer EXCEPT for one case: a requisition
           that was drafted, never faxed, and whose row is still live keeps its
           Review & send below (the route reuses the drafted report — #694). */
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
          display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          color: '#0f766e', fontSize: 13, fontWeight: 600,
        }}>
          {sent.sent
            ? `✓ ${recipientContact.fax || recipientContact.email ? `faxed/emailed to ${recipientContact.fax || recipientContact.email}` : 'sent in Elation'}`
            : `✓ drafted in Elation${sent.elationId != null ? ` #${sent.elationId}` : ''}`}
        </div>
      )}
      {sent && !faxableAfterDraft ? null : card.type === 'quest_homebound' || isGeneric ? (
        <>
          {/* O61 — blank ICD-10 lines WARN, they never block. Same sentence above
              the PDF preview, so it is on screen at the moment of the fax too. */}
          {card.type === 'quest_homebound' && dxWarning && (
            <div data-testid="blank-dx-warning" style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
              ⚠ {dxWarning}
            </div>
          )}
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
            display: 'flex', gap: 8, justifyContent: 'flex-end',
          }}>
            {/* Already drafted — there is nothing left to draft, only to fax. */}
            {!sent && (
            <button
              type="button"
              disabled={submitting}
              data-action="confirm-card"
              onClick={() => doSubmit({ mode: 'draft' })}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#374151', background: '#fff',
                border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Confirm
            </button>
            )}
            <button
              type="button"
              disabled={submitting || editsInFlight > 0}
              onClick={openPreview}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#fff', background: '#0f766e',
                border: 'none', borderRadius: 8, cursor: 'pointer',
              }}
            >
              {sendLabel}
            </button>
          </div>

          {/* Inline requisition preview — no fax until the explicit Confirm. */}
          {preview && (
            <div style={{ marginTop: 10 }}>
              {preview.loading && <div style={{ color: '#6b7280' }}>Rendering requisition…</div>}
              {preview.errors && (
                <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{preview.errors.join(' ')}</div>
              )}
              {preview.url && (
                <>
                  <iframe
                    title={isGeneric ? 'Lab requisition preview' : 'Quest requisition preview'}
                    src={preview.url}
                    style={{ width: '100%', height: 460, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
                  />
                  {(preview.warnings || []).map((w, i) => (
                    <div key={i} style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>⚠ {w}</div>
                  ))}
                  {/* O61 again, right where the fax is confirmed: the document
                      above shows the empty ICD-10 cells, this names how many. */}
                  {card.type === 'quest_homebound' && dxWarning && (
                    <div data-testid="blank-dx-warning-preview" style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                      ⚠ {dxWarning}
                    </div>
                  )}
                  {/* Destination shown BEFORE the irreversible Confirm & fax — where
                      this goes. Generic labs get an editable one-off override. */}
                  {isGeneric ? (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Will fax to <b>{preview.recipientName || card.genericLab.name}</b> ·</span>
                      <input
                        type="text"
                        value={faxEdit}
                        onChange={(e) => setFaxEdit(e.target.value)}
                        placeholder="fax number"
                        aria-label="Destination fax number"
                        style={{
                          fontSize: 12, padding: '3px 6px', width: 120,
                          border: `1px solid ${canConfirmFax ? '#d1d5db' : '#f59e0b'}`, borderRadius: 6,
                        }}
                      />
                      {preview.fax && faxEdit.replace(/\D/g, '') !== String(preview.fax).replace(/\D/g, '') && (
                        <span style={{ color: '#b45309' }}>(one-off override)</span>
                      )}
                    </div>
                  ) : preview.fax && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>
                      Will fax to <b>{preview.recipientName || 'Quest Mobile Phlebotomy'}</b> · {preview.fax}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={closePreview}
                      style={{
                        padding: '7px 14px', fontSize: 13, fontWeight: 600,
                        color: '#374151', background: '#fff',
                        border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={submitting || !canConfirmFax}
                      onClick={confirmFax}
                      title={canConfirmFax ? undefined : 'Enter a 10-11 digit fax number'}
                      style={{
                        padding: '7px 14px', fontSize: 13, fontWeight: 600,
                        color: '#fff', background: canConfirmFax ? '#0f766e' : '#9ca3af',
                        border: 'none', borderRadius: 8, cursor: canConfirmFax ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {isGeneric ? `Confirm & fax to ${card.genericLab.name}` : 'Confirm & fax to Quest'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      ) : confirmingSend ? (
        /* Send now confirm — shows the destination before the irreversible sign +
           transmit. No requisition PDF (referrals/facility labs have none, unlike
           Quest), so the destination read IS the confirm. */
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
        }}>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
            {recipientContact.email && !recipientContact.fax
              ? <>Will email to <b>{recipientContact.email}</b></>
              : (recipientContact.fax
                  ? <>Will fax to <b>{recipientContact.fax}</b></>
                  : 'Will sign and transmit this order in Elation')}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              disabled={sending}
              onClick={() => setConfirmingSend(false)}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#374151', background: '#fff',
                border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={sending}
              onClick={() => doSend('send')}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#fff', background: sending ? '#9ca3af' : '#0f766e',
                border: 'none', borderRadius: 8, cursor: sending ? 'wait' : 'pointer',
              }}
            >
              {sending ? 'Sending…' : 'Confirm'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
          display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
        }}>
          <span style={{ marginRight: 'auto', fontSize: 12, color: control && control.enabled === false && control.reason ? '#92400e' : '#9ca3af' }}>
            {control && control.enabled === false && control.reason
              ? control.reason
              : 'Creates an unsigned Elation draft to review & sign.'}
          </span>
          <button
            type="button"
            disabled={sending || submitting || !onSend || (control ? control.enabled === false : false)}
            title={control && control.enabled === false ? control.reason : undefined}
            data-action="confirm-card"
            onClick={() => doSend('draft')}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              color: '#fff',
              background: (sending || (control && control.enabled === false)) ? '#9ca3af' : '#0f766e',
              border: 'none', borderRadius: 8,
              cursor: sending ? 'wait' : ((control && control.enabled === false) ? 'not-allowed' : 'pointer'),
            }}
          >
            {sending ? 'Creating…' : 'Confirm'}
          </button>
          {/* Send now — only for recipient-routed (facility) requisitions that
              carry a destination; plain e-Req labs with no recipient draft only. */}
          {showDestination && (
            <button
              type="button"
              disabled={sending || submitting || !onSend}
              onClick={() => setConfirmingSend(true)}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#0f766e', background: '#fff',
                border: '1px solid #0f766e', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Send now
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default LabOrderCard;
